import type { RestClient } from "@shopify/shopify-api"

export interface Blog {
  id?: string | number
  handle?: string
  title?: string
  commentable?: string
  feedburner?: string | null
  feedburner_location?: string | null
  tags?: string
  template_suffix?: string | null
  created_at?: string
  updated_at?: string
  admin_graphql_api_id?: string
}

export interface Article {
  id?: string | number
  blog_id?: number
  title?: string
  author?: string
  handle?: string
  body_html?: string
  summary_html?: string
  image?: {
    src?: string
    alt?: string
    attachment?: string
  } | null
  published?: boolean
  published_at?: string | null
  created_at?: string
  updated_at?: string
  tags?: string
  template_suffix?: string | null
  admin_graphql_api_id?: string
  metafields?: Metafield[]
}

export interface Metafield {
  id?: string | number
  key?: string
  namespace?: string
  value?: string
  value_type?: string
  type?: string
  description?: string | null
  owner_id?: number
  owner_resource?: string
  created_at?: string
  updated_at?: string
  admin_graphql_api_id?: string
}

export interface ApplicationCharge {
  id?: number
  name?: string
  status?: string
  test?: boolean
  price?: string
  confirmation_url?: string
  return_url?: string
  capped_amount?: string
  terms?: string
  created_at?: string
  updated_at?: string
}

export interface UsageCharge {
  id?: number
  description?: string
  price?: string
  created_at?: string
}

export interface Sync {
  date: string
  articles_synced: number
}
export interface ShopCore {
  billing_last?: string
  billing_active: boolean
  domain: string
}
// TODO organize this better
interface ShopCoreBackend extends ShopCore {
  last_sync: Sync[]
  current_charge_id: string
  name: string
}
export interface ShopFrontend extends ShopCore {
  connections: ConnectionFrontend[]
  locale?: string
  // this app load repaired the shop's connection to Shopify
  session_restored?: boolean
  dismissed_release?: string
}
interface Connection {
  unconfirmed_by: string
}
export interface Parent extends Connection {
  parent_domain: string
  parent: ShopCoreBackend
}
export interface Child extends Connection {
  child: ShopCoreBackend
}
export type ConnectedShop = Parent | Child
export enum ConnectionStatus {
  PENDING = "PENDING",
  ACTIVE = "ACTIVE",
  RECEIVED = "RECEIVED",
}
export enum ConnectionTrigger {
  AUTOMATIC = "AUTOMATIC",
  MANUAL = "MANUAL",
}
export interface ConnectionFrontend {
  status: ConnectionStatus
  domain: string
  name?: string
  trigger?: ConnectionTrigger
  direction: SyncDirection
  last_sync?: Sync
  parent_auth_status?: ShopAuthStatus
  child_auth_status?: ShopAuthStatus
}

export enum SyncDirection {
  TO = "to",
  FROM = "from",
}

export enum ConnectionAction {
  INVITE = "invite",
  ACCEPT = "accept",
  DELETE = "delete",
}

export interface ConnectionPayload {
  domain: string
  direction: SyncDirection
}

export interface SyncPayload {
  sync_domain: string
  previewPricing?: boolean
  forceUpdate?: string
  // When the previewed selection was made and how many articles it held.
  // On a preview request they ask for that selection's charge; on the sync
  // after the payment they pin what is copied to it
  selectedAt?: string
  confirmCount?: string
  // the preview's signature, sent back with it to have the charge created
  signature?: string
}

export interface SyncPreviewArticle {
  title: string
  blog: string
  isNew: boolean
}

export interface SyncPreview {
  selected_at: string
  count: number
  signature: string
  price: number
  articles: SyncPreviewArticle[]
}

// One line of the sync endpoint's newline-delimited JSON response
export type SyncEvent =
  | { type: "status"; message: string }
  | { type: "error"; sentry_id?: string }
  | {
      type: "result"
      preview?: SyncPreview
      confirmation_url?: string
      articles_synced?: number
      date?: string
    }

export enum Endpoint {
  CONNECTION = "/connection",
  SUBSCRIPTION = "/subscription",
  SYNC = "/sync",
  SYNC_ALL = "/syncAll",
  EVENTS = "/events",
  BLOGS = "/blogs",
  RELEASE_DISMISS = "/release/dismiss",
}

export enum PageRoute {
  CONFIRM = "/confirm-payment",
  PRICING = "/pricing",
}

export interface SyncShop {
  access_token: string
  domain: string
  shopifyClient: RestClient
  blogs?: Blog[]
}

export interface SyncChild extends SyncShop {
  articles_synced_current_billing_cycle: number
  last_sync: string
  articles_synced?: number
}

export interface BlogsResponse {
  blogs: Blog[]
}

export interface BlogResponse {
  blog: Blog
}

export interface ModifiedArticle extends Article {
  child_connections?: { blog_id: string; child_id: string }[]
}

export interface ArticlesResponse {
  articles: ModifiedArticle[]
}

export interface ArticleResponse {
  article: Article
}

export interface MetafieldsResponse {
  metafields: Metafield[]
}

export interface MetafieldResponse {
  metafield: Metafield
}

export interface ApplicationChargeResponse {
  application_charge: ApplicationCharge
}

export interface RecurringChargesResponse {
  recurring_application_charges?: ApplicationCharge[]
}

export interface UsageChargeResponse {
  usage_charge: UsageCharge
}

export interface BlogCoreData {
  commentable: string
  feedburner?: string
  feedburner_location?: string
  handle: string
  tags?: string
  template_suffix?: string
  title: string
}

export interface ArticleCoreData {
  author: string
  body_html: string
  handle: string
  image?: {
    src: string
    alt: string
    attachment: string
  }
  published: boolean
  published_at?: string
  summary_html: string
  tags?: string
  template_suffix?: string
  title: string
}

export interface SubscriptionPostPayload {
  charge_id: string
}

export interface HomeStateSync {
  syncConfirmation: boolean
  syncDomain: string
  forceUpdate: boolean
  selectedAt?: string
  confirmCount?: number
  newlyAutomatic?: boolean
}

interface BasicBlog {
  id: string
  title: string
  handle: string
}
// "recovery": the merchant should open the app so a real session can be
// stored, but syncing continues on application-only tokens meanwhile.
// "unknown": session storage could not be read, so the shop's state is not
// known. It must not be shown to merchants as needing re-authorisation.
export type ShopAuthStatus = "ok" | "reauth_required" | "recovery" | "unknown"
export interface ConnectionInfoResponse {
  blog_connections: BlogConnection[]
  // newline-separated, as stored on the connection
  ignored_tags: string | null
  parent_blogs: BasicBlog[]
  child_blogs: BasicBlog[]
  parent_auth_status: ShopAuthStatus
  child_auth_status: ShopAuthStatus
}

export interface BlogConnection {
  pending: boolean
  child_id?: string
  parent_id: string
  sync_after?: string
  synced_through?: string | null
}

export interface ArticleConnection {
  parent_id: string
  child_id: string
  blog_id?: string
}

interface BlogSettings {
  blog?: string
  date?: string
}
export type BlogSettingsChanges = Record<
  string,
  { newInfo?: BlogSettings; oldInfo: BlogSettings }
>

// body of POST Endpoint.BLOGS; ignored_tags is only sent when it changed
export interface BlogSettingsSave {
  blogs: BlogSettingsChanges
  ignored_tags?: string
}
